import React from 'react';
// src/App.tsx
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastProvider } from './contexts/ToastContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import { AuthProvider } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { LayoutProvider } from './contexts/LayoutContext';
import { SchoolConfigProvider } from './contexts/SchoolConfigContext';
// ✅ REPLACED: Use stabilized providers instead of original ones
import { StabilizedGridProvider, StabilizedAllocationProvider } from './contexts/StabilizedContexts';
// Keep the original providers as fallback imports
import { GridProvider } from './contexts/GridContext';
import { GridsDataProvider } from './contexts/GridsDataContext';
import { AllocationProvider } from './contexts/AllocationContext';
// ✅ NEW: Add MultiGridProvider
import { MultiGridProvider } from './contexts/MultiGridContext';
// ✅ NEW: Add GeminiProvider
import { GeminiProvider } from './contexts/GeminiContext';
// ✅ NEW: Add DatabaseProvider for database switching
import { DatabaseProvider } from './contexts/DatabaseContext';
// ✅ NEW: Add AcademicYearProvider for year-based grid compartmentalization
import { AcademicYearProvider } from './contexts/AcademicYearContext';
import Login from './pages/Login';
import AuthSso from './pages/AuthSso';
import Dashboard from './pages/EnhancedDashboard';
import StaffDirectory from './pages/staff/StaffDirectory';
import StaffAllowances from './pages/staff/StaffAllowances';
import LoadSummaries from './pages/staff/LoadSummaries';
import GridImport from './pages/grid/GridImport';
import EnhancedMultiGridEditor from './pages/grid/EnhancedMultiGridEditor';
import ConflictResolution from './pages/grid/ConflictResolution';
import LineSubjectConfig from './pages/grid/LineSubjectConfig';
import GridSetupWizard from './pages/grid/GridSetupWizard';
import BulkOperations from './pages/allocation/BulkOperations';
import AssignmentHistory from './pages/allocation/AssignmentHistory';
import LetterGeneration from './pages/loadletter/LetterGeneration';
import StaffDashboard from './pages/loadletter/StaffDashboard';
import TemplateSettings from './pages/loadletter/TemplateSettings';
import ReportsHub from './pages/reports/ReportsHub';
import PreferenceSatisfaction from './pages/reports/PreferenceSatisfaction';
import WorkloadEquity from './pages/reports/WorkloadEquity';
import RecruitmentGaps from './pages/reports/RecruitmentGaps';
import TeachingIntentionsReports from './pages/reports/TeachingIntentionsReports';
import StaffByTeaching from './pages/reports/StaffByTeaching';
import PreferenceMatchReport from './pages/reports/PreferenceMatchReport';
import RuahTermComparison from './pages/reports/RuahTermComparison';
import TimetabledSubsReport from './pages/reports/TimetabledSubsReport';
import StaffFteReport from './pages/reports/StaffFteReport';
import FteDaysOffReport from './pages/reports/FteDaysOffReport';
import AllowanceTemplates from './pages/settings/AllowanceTemplates';
import OpenAIConfiguration from './pages/settings/OpenAIConfiguration';
import ComprehensiveUserManagement from './pages/settings/ComprehensiveUserManagement';
import RoleManagement from './pages/settings/RoleManagement';
import GridTypeManagement from './pages/settings/GridTypeManagement';
import SchoolSettings from './pages/admin/SchoolSettings';
import IntentionPeriods from './pages/admin/IntentionPeriods';
import SubmitIntentions from './pages/intentions/SubmitIntentions';
import AdminFillIntention from './pages/intentions/AdminFillIntention';
import IntentionReview from './pages/hr/IntentionReview';
import DiscussQueue from './pages/hr/DiscussQueue';
// ✅ UPDATED: Replace MainLayout with SidebarLayout
import SidebarLayout from './layouts/SidebarLayout';
// Import new curriculum preferences page
import CurriculumAssignments from './pages/curriculum/CurriculumAssignments';
import ReadOnlyGrids from './pages/curriculum/ReadOnlyGrids';
import ClassManagement from './pages/grid/ClassManagement';
// Import new teaching intentions page
import TeachingIntentionsPage from './pages/intentions/TeachingIntentions';
// Import data import page
import ImportData from './pages/ImportData';
// Import admin setup component
import NotFound from './pages/NotFound';
import { VersionBadge } from './components/VersionBadge';
export function App() {
  return <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      {/* DatabaseProvider MUST be at the top level for database switching */}
      <DatabaseProvider>
        {/* ToastProvider MUST be at the top level */}
        <ToastProvider>
        {/* Promise-based confirm dialogs (styled replacement for window.confirm) */}
        <ConfirmProvider>
        {/* AuthProvider can now safely use useToast if needed */}
        <AuthProvider>
          {/* All other providers can use both useAuth and useToast */}
          <DataProvider>
            {/* School Configuration Provider - loads school-specific settings */}
            <SchoolConfigProvider>
            {/* ✅ NEW: Add LayoutProvider for sidebar control */}
            <LayoutProvider>
              {/* ✅ NEW: Add GeminiProvider for AI functionality */}
              <GeminiProvider>
                {/* Single shared grids fetch + realtime subscription for all three grid providers */}
                <GridsDataProvider>
                {/* ✅ HYBRID: Keep both old and new providers during transition */}
                <GridProvider>
                  {/* AcademicYearProvider must sit ABOVE AllocationProvider:
                      AllocationContext's load calculation calls useAcademicYear()
                      to scope allowances to the working year, and that hook throws
                      if its provider is not an ancestor. AcademicYearProvider itself
                      depends on nothing but React and supabase, so hoisting it is
                      safe. */}
                  <AcademicYearProvider>
                    <AllocationProvider>
                    {/* ✅ NEW: Add MultiGridProvider for advanced grid management */}
                    <MultiGridProvider>
                      {/* ✅ NEW: Add stabilized providers on top of existing ones */}
                      <StabilizedGridProvider>
                        <StabilizedAllocationProvider>
                        <Routes>
                          <Route path="/login" element={<Login />} />
                          <Route path="/auth/sso" element={<AuthSso />} />
                          {/* /admin-setup deliberately removed. It was a PUBLIC route --
                              declared here alongside /login, OUTSIDE the authenticated
                              block below -- whose only purpose was creating admin users.
                              It survived because it was broken: setupAdmin.ts still called
                              Firebase (query/collection/db) after the Supabase migration,
                              so it threw ReferenceError before doing anything. Repairing it
                              would have shipped unauthenticated admin creation on a
                              multi-tenant platform. Admins are managed at
                              /admin/user-management, inside the protected block. */}
                          {/* ✅ UPDATED: Protected routes now use SidebarLayout */}
                          <Route path="/" element={<SidebarLayout />}>
                            <Route index element={<Navigate to="/dashboard" replace />} />
                            <Route path="dashboard" element={<Dashboard />} />
                            {/* Staff Management */}
                            <Route path="staff">
                              <Route path="directory" element={<StaffDirectory />} />
                              <Route path="allowances" element={<StaffAllowances />} />
                              <Route path="load-summaries" element={<LoadSummaries />} />
                              <Route path="import-data" element={<ImportData />} />
                            </Route>
                            {/* NEW: Curriculum Management */}
                            <Route path="curriculum">
                              <Route path="assignments" element={<CurriculumAssignments />} />
                              <Route path="grids" element={<ReadOnlyGrids />} />
                            </Route>
                            {/* NEW: Teaching Intentions */}
                            <Route path="intentions">
                              <Route index element={<TeachingIntentionsPage />} />
                              <Route path="submit" element={<SubmitIntentions />} />
                              <Route path="admin/fill" element={<AdminFillIntention />} />
                            </Route>
                            {/* HR Routes */}
                            <Route path="hr">
                              <Route path="intention-review" element={<IntentionReview />} />
                              <Route path="discuss-queue" element={<DiscussQueue />} />
                            </Route>
                            {/* Class Allocation (Merged Grid Management & Allocation) */}
                            <Route path="grid">
                              <Route path="setup" element={<GridSetupWizard />} />
                              <Route path="import" element={<GridImport />} />
                              <Route path="multi-editor" element={<EnhancedMultiGridEditor />} />
                              <Route path="class-management" element={<ClassManagement />} />
                              <Route path="subject-config" element={<LineSubjectConfig />} />
                              <Route path="conflicts" element={<ConflictResolution />} />
                            </Route>
                            {/* Allocation System (Advanced features) */}
                            <Route path="allocation">
                              <Route path="bulk" element={<BulkOperations />} />
                              <Route path="history" element={<AssignmentHistory />} />
                            </Route>
                            {/* Load Letter System */}
                            <Route path="load-letter">
                              <Route path="generation" element={<LetterGeneration />} />
                              <Route path="template-settings" element={<TemplateSettings />} />
                              <Route path="staff-dashboard" element={<StaffDashboard />} />
                            </Route>
                            {/* Reports & Analytics */}
                            <Route path="reports">
                              <Route index element={<ReportsHub />} />
                              <Route path="preference-satisfaction" element={<PreferenceSatisfaction />} />
                              <Route path="workload-equity" element={<WorkloadEquity />} />
                              <Route path="recruitment-gaps" element={<RecruitmentGaps />} />
                              <Route path="teaching-intentions" element={<TeachingIntentionsReports />} />
                              <Route path="staff-by-teaching" element={<StaffByTeaching />} />
                              <Route path="preference-match" element={<PreferenceMatchReport />} />
                              <Route path="ruah-term-comparison" element={<RuahTermComparison />} />
                              <Route path="timetabled-subs" element={<TimetabledSubsReport />} />
                              <Route path="staff-fte" element={<StaffFteReport />} />
                              <Route path="fte-days-off" element={<FteDaysOffReport />} />
                            </Route>
                            {/* Admin Settings - School Configuration */}
                            <Route path="admin">
                              <Route path="school-settings" element={<SchoolSettings />} />
                              <Route path="intention-periods" element={<IntentionPeriods />} />
                            </Route>

                            <Route path="settings">
                              {/* Settings home is the canonical School Settings page. */}
                              <Route index element={<Navigate to="/admin/school-settings" replace />} />
                              {/* Departments consolidated into Learning Areas (School Settings). */}
                              <Route path="departments" element={<Navigate to="/admin/school-settings" replace />} />
                              <Route path="grid-types" element={<GridTypeManagement />} />
                              <Route path="allowance-templates" element={<AllowanceTemplates />} />
                              <Route path="user-management" element={<ComprehensiveUserManagement />} />
                              <Route path="role-management" element={<RoleManagement />} />
                              <Route path="openai-configuration" element={<OpenAIConfiguration />} />
                            </Route>
                            {/* Catch-all: unknown paths render a friendly 404 inside the shell */}
                            <Route path="*" element={<NotFound />} />
                          </Route>
                        </Routes>
                        <VersionBadge />
                        </StabilizedAllocationProvider>
                      </StabilizedGridProvider>
                      </MultiGridProvider>
                    </AllocationProvider>
                    </AcademicYearProvider>
                  </GridProvider>
                </GridsDataProvider>
              </GeminiProvider>
            </LayoutProvider>
            </SchoolConfigProvider>
          </DataProvider>
        </AuthProvider>
        </ConfirmProvider>
        </ToastProvider>
      </DatabaseProvider>
    </Router>;
}